package kz.dentalcrm.app.navigation

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map

/**
 * A process-local channel for one-off navigation "results".
 * 
 * ## Concept
 * Results are "return values" from a screen when navigating back.
 * A screen publishes via [set], another screen observes via [results] / [resultFlow].
 * 
 * ## Example (PatientDetail -> Patients)
 * Producer:
 * ```
 * navigator.navigateBackWithResult(
 *   key = PatientDetailRoute.RESULT_KEY,
 *   result = PatientDetailRoute.Result(...),
 * )
 * ```
 * 
 * Consumer:
 * ```
 * results.resultFlow<PatientDetailRoute.Result>(PatientDetailRoute.RESULT_KEY).collect { ... }
 * ```
 * 
 * ## Delivery semantics
 * - Results are replayed (consumer receives value even if it starts collecting after emission)
 * - Consumers should call [clear] after handling a result to avoid re-delivery
 */
interface NavigationResults {

    /**
     * Stream of emitted result events.
     * 
     * Consumers typically filter by [ResultEvent.key] and cast [ResultEvent.value]
     * to the expected type (use [resultFlow] for a typed API).
     */
    val results: Flow<ResultEvent>

    /**
     * A single result emission.
     * 
     * @property key The result channel identifier (e.g. `PatientDetailRoute.RESULT_KEY`)
     * @property value The payload. The consumer must know its type.
     */
    data class ResultEvent(
        val key: String,
        val value: Any,
    )

    /**
     * Publishes a result under [key].
     * 
     * Consumers observing that key will receive the value.
     */
    fun set(key: String, value: Any)

    /**
     * Clears a previously published result under [key].
     * 
     * After a consumer handles the result, it should clear it to avoid re-delivery.
     */
    fun clear(key: String)
}

/**
 * Convenience API for consuming typed navigation results.
 * 
 * ## Usage
 * ```
 * LaunchedEffect(results) {
 *   results.resultFlow<PatientDetailRoute.Result>(PatientDetailRoute.RESULT_KEY)
 *     .collect { value ->
 *       // handle value
 *       results.clear(PatientDetailRoute.RESULT_KEY)
 *     }
 * }
 * ```
 * 
 * ## Contracts
 * - The producer and consumer must agree on:
 *   - the [key]
 *   - the result type [T]
 * 
 * If the emitted value is not of type [T], this will throw with a descriptive error.
 */
inline fun <reified T : Any> NavigationResults.resultFlow(key: String): Flow<T> {
    return results
        .filter { it.key == key }
        .filter { it.value !is Unit } // Ignore clear() events which emit Unit as a sentinel
        .map { event ->
            event.value as? T
                ?: error("Navigation result for key='$key' has incorrect type. Expected ${T::class.simpleName ?: "Unknown"}, but got ${event.value::class.simpleName ?: "Unknown"}")
        }
}
